import { useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { Section, Field, StatusBadge, fmtUsd, fmtDate, EmptyRow } from "./shared";

function BalanceAdjust({ user, onDone }) {
    const [form, setForm] = useState({ amount: "", note: "" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        const amount = Number(form.amount);
        if (!amount) return toast.error("Geçerli bir tutar girin (+/-)");
        setLoading(true);
        try {
            await api.post("/admin/transactions/adjust-balance", { user_id: user.id, amount, note: form.note || undefined });
            toast.success(`Bakiye güncellendi (${amount > 0 ? "+" : ""}${amount.toFixed(2)} USD)`);
            setForm({ amount: "", note: "" });
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bakiye güncellenemedi");
        } finally {
            setLoading(false);
        }
    };
    return (
        <form onSubmit={submit} className="grid md:grid-cols-[160px_1fr_auto] gap-3 items-end" data-testid="wallet-adjust-form">
            <Field label="Tutar (USD, +/-)"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="wallet-adjust-amount" placeholder="50 veya -50" className="bg-background border-white/10 h-9" required /></Field>
            <Field label="Not"><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="wallet-adjust-note" placeholder="Opsiyonel" className="bg-background border-white/10 h-9" /></Field>
            <Button type="submit" disabled={loading} data-testid="wallet-adjust-submit" className="bg-primary text-primary-foreground h-9">{loading ? "…" : "Uygula"}</Button>
        </form>
    );
}

export function UserWalletTab({ user, wallet, addresses, deposits, onChanged }) {
    const copy = (txt) => navigator.clipboard?.writeText(txt).then(() => toast.success("Kopyalandı")).catch(() => toast.error("Kopyalanamadı"));
    return (
        <div className="space-y-4">
            <Section title="Cüzdan bakiyesi" subtitle={`Aylık harcama ${fmtUsd(wallet.monthly_spend)} · Toplam harcama ${fmtUsd(wallet.lifetime_spend)} · Kademe ${wallet.current_tier}`} testid="wallet-section">
                <div className="grid grid-cols-2 gap-3 mb-2">
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                        <p className="text-[11px] text-muted-foreground">USD bakiye</p>
                        <p className="font-display font-bold text-2xl text-emerald-300" data-testid="wallet-usd-balance">{fmtUsd(wallet.usd_balance)}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-background/40 p-3">
                        <p className="text-[11px] text-muted-foreground">Cashback bakiyesi</p>
                        <p className="font-display font-bold text-2xl" data-testid="wallet-cashback-balance">{fmtUsd(wallet.cashback_balance)}</p>
                    </div>
                </div>
                <BalanceAdjust user={user} onDone={onChanged} />
            </Section>

            <Section title="Yatırım adresleri (HD)" subtitle="Kullanıcıya atanmış deterministik adresler." testid="wallet-addresses">
                <div className="space-y-2">
                    {addresses.map((a) => (
                        <div key={a.currency} className="flex items-center gap-2 text-xs" data-testid={`wallet-address-${a.currency}`}>
                            <span className="w-28 font-mono text-muted-foreground shrink-0">{a.label}</span>
                            <code className="flex-1 truncate px-2.5 py-1.5 rounded-lg bg-background/60 border border-white/10 font-mono">{a.address}</code>
                            <button type="button" onClick={() => copy(a.address)} className="p-1.5 rounded-lg border border-white/10 hover:bg-white/[0.06]"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title={`Gelen yatırımlar (${deposits.length})`} testid="wallet-deposits">
                <div className="overflow-x-auto -mx-5">
                    <table className="w-full text-sm min-w-[640px]">
                        <thead className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            <tr><th className="text-left px-5 py-2">Tarih</th><th className="text-left py-2">Coin</th><th className="text-right py-2">USD</th><th className="text-left py-2 pl-4">Durum</th><th className="text-left py-2 pl-4">Tx</th></tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {deposits.length === 0 && <EmptyRow colSpan={5} text="Henüz yatırım yok." />}
                            {deposits.map((d) => (
                                <tr key={d.id} data-testid={`deposit-row-${d.id}`}>
                                    <td className="px-5 py-2 text-xs text-muted-foreground">{fmtDate(d.first_seen_at)}</td>
                                    <td className="py-2 font-mono text-xs">{d.currency}</td>
                                    <td className="py-2 text-right font-semibold">{fmtUsd(d.amount_usd)}</td>
                                    <td className="py-2 pl-4"><StatusBadge value={d.status} /> <span className="text-[11px] text-muted-foreground ml-1">{d.confirmations}/{d.confirmations_required}</span></td>
                                    <td className="py-2 pl-4 text-xs font-mono">
                                        {d.tx_hash ? <a href={d.explorer_url || "#"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{d.tx_hash.slice(0, 10)}… <ExternalLink className="w-3 h-3" /></a> : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
        </div>
    );
}
